import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authManager } from '../utils/auth';
import MemberSearchField from '../Components/MemberSearchField';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Must match somo-circle1-api's ACCEPTED_FILE_TYPES — same convention as the
// member-facing upload in somo-circle1-membership-frontend's
// Screens/Programs/Loyalty.js. HEIC/HEIF is converted to JPEG server-side
// (somo-circle1-api#667); TIFF is read by Textract natively — neither needs
// to be blocked client-side.
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif', 'image/tiff'];
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

// Real-world gap (somo-circle1-membership-frontend#608): Windows has no
// built-in MIME registration for HEIC unless the optional "HEIF Image
// Extensions" codec pack is installed, and Chromium derives File.type from
// the OS's registered type-by-extension mapping on Windows — so an admin
// picking a genuine .heic file via the browser's file input commonly gets
// file.type === '' there, failing ACCEPTED_FILE_TYPES.includes() even
// though the file is perfectly valid. Same class of fix as the API's own
// resolveAttachmentContentType (loyalty.controller.js /
// lambda-functions/loyalty-center/index.mjs) for a mail client mislabeling
// a HEIC attachment as application/octet-stream.
const GENERIC_FILE_TYPES = new Set(['', 'application/octet-stream']);
const EXTENSION_TO_FILE_TYPE = {
    heic: 'image/heic',
    heif: 'image/heif',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf',
    tif: 'image/tiff',
    tiff: 'image/tiff',
};

// Resolves a generic/empty declared file.type to the real one, inferred
// from the filename extension. Returns the original type unchanged when
// it's already something specific — never overrides a legitimate, explicit
// browser-reported type, only fills in for the generic/empty case.
const resolveFileType = (file) => {
    if (!GENERIC_FILE_TYPES.has(file.type)) return file.type;
    const extension = (file.name || '').split('.').pop()?.toLowerCase();
    return EXTENSION_TO_FILE_TYPE[extension] || file.type;
};

const UploadLoyaltyReceipt = () => {
    const navigate = useNavigate();

    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [lastReceipt, setLastReceipt] = useState(null);

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setLastReceipt(null);
        const resolvedType = resolveFileType(file);
        if (!ACCEPTED_FILE_TYPES.includes(resolvedType)) {
            setUploadError('Please choose a JPEG, PNG, PDF, TIFF, HEIC, or HEIF file.');
            setSelectedFile(null);
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setUploadError('That file is larger than 20MB. Please choose a smaller photo or PDF.');
            setSelectedFile(null);
            return;
        }
        setUploadError('');
        // Store the file with its CORRECTED type from here on, so
        // handleUpload's own file.type reads (the presign request body and
        // the actual multipart upload's Content-Type — S3's presigned POST
        // policy requires those to exactly match, and FormData derives the
        // upload's header from the File/Blob's own .type) are already right
        // without handleUpload needing to know anything was resolved.
        setSelectedFile(resolvedType === file.type ? file : new File([file], file.name, { type: resolvedType }));
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setUploadError('');
    };

    // Upload only fires on an explicit button press (not on file selection) —
    // an admin choosing the wrong file by mistake should be able to swap it
    // before anything actually goes out, not have it submitted immediately.
    const handleUpload = async () => {
        const file = selectedFile;
        if (!file || !selectedMember) return;

        setUploadError('');
        setLastReceipt(null);
        setUploading(true);
        try {
            const presignRes = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts/presign`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: selectedMember._id, fileName: file.name, fileType: file.type,
                    }),
                },
            );
            const presignData = await presignRes.json();
            if (!presignRes.ok || !presignData.uploadUrl || !presignData.fields || !presignData.fileUrl) {
                throw new Error(presignData.message || 'Could not prepare the upload. Please try again.');
            }

            // S3 POST policy requires the 'file' field to be added last — anything
            // appended after it is ignored by S3.
            const formData = new FormData();
            Object.entries(presignData.fields).forEach(([field, value]) => {
                formData.append(field, value);
            });
            formData.append('file', file);

            const s3Res = await fetch(presignData.uploadUrl, { method: 'POST', body: formData });
            if (!s3Res.ok) {
                throw new Error('Upload failed. Please try again.');
            }

            const createRes = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: selectedMember._id, key: presignData.key, fileName: file.name,
                    }),
                },
            );
            const createData = await createRes.json();
            if (!createRes.ok) {
                throw new Error(createData.message || 'Could not submit the receipt. Please try again.');
            }

            setSelectedFile(null);
            setLastReceipt(createData.data);
            toast.success('Receipt uploaded and submitted for review.');
        } catch (err) {
            setUploadError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const uploadAnother = () => {
        setLastReceipt(null);
        setSelectedFile(null);
        setUploadError('');
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Upload Loyalty Receipt</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/loyaltyReceipts"><i className="fa-solid fa-arrow-left-long" /> Back to Receipts</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    <div className="card-body">

                                        <MemberSearchField
                                            onSelect={setSelectedMember}
                                            disabled={uploading}
                                        />

                                        {/* Choose file */}
                                        <div className="form-group row">
                                            <label className="col-12 col-md-4 col-form-label">Receipt file<span className="f_red">*</span></label>
                                            <div className="col-12 col-md-8">
                                                <div className="s_profileupld">
                                                    <label
                                                        className="btn-bs-file btn-blue mb-0"
                                                        style={!selectedMember || uploading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                                                    >
                                                        <i className="fa-solid fa-upload" /> Choose File
                                                        <input
                                                            type="file"
                                                            aria-label="Receipt file"
                                                            accept={ACCEPTED_FILE_TYPES.join(',')}
                                                            onChange={handleFileSelected}
                                                            disabled={!selectedMember || uploading}
                                                        />
                                                    </label>
                                                </div>
                                                <small className="text-muted d-block mt-1">
                                                    {!selectedMember ? 'Select a member first.' : 'JPEG, PNG, PDF, TIFF, HEIC, or HEIF — up to 20MB.'}
                                                </small>
                                                {uploadError && <small className="text-danger d-block mt-1">{uploadError}</small>}
                                            </div>
                                        </div>

                                        {/* Chosen file preview — upload only happens when the admin
                                            explicitly confirms, so picking the wrong file by mistake
                                            can be undone before anything is actually submitted. */}
                                        {selectedFile && (
                                            <div className="form-group row">
                                                <div className="col-12 col-md-8 offset-md-4">
                                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                                        <span>
                                                            <i className="fa-solid fa-paperclip" /> {selectedFile.name}
                                                            {' '}
                                                            <small className="text-muted">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</small>
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={clearSelectedFile}
                                                            disabled={uploading}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-blue mt-2"
                                                        onClick={handleUpload}
                                                        disabled={uploading}
                                                    >
                                                        {uploading ? 'Uploading…' : 'Upload Receipt'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Result */}
                                        {lastReceipt && (
                                            <div className="form-group row">
                                                <div className="col-12 col-md-8 offset-md-4">
                                                    <div className="alert alert-success">
                                                        <strong>Receipt submitted for review.</strong>
                                                        <div className="mt-2">
                                                            {lastReceipt.ocrStatus === 'completed' && lastReceipt.grossTotal != null && (
                                                                <span>OCR read a total of <strong>${Number(lastReceipt.grossTotal).toFixed(2)}</strong>.</span>
                                                            )}
                                                            {lastReceipt.ocrStatus === 'failed' && <span>OCR could not read this receipt — it will need manual review.</span>}
                                                            {lastReceipt.ocrStatus === 'not_commissary' && <span>OCR did not detect a commissary line item — flagged for review.</span>}
                                                            {lastReceipt.ocrStatus === 'unsupported' && <span>This file type isn&apos;t supported by OCR — it will need manual review.</span>}
                                                            {lastReceipt.ocrStatus === 'conversion_failed' && (
                                                                <span>This HEIC/HEIF file couldn&apos;t be converted, so OCR never ran — it will need manual review.</span>
                                                            )}
                                                            {lastReceipt.possibleDuplicate && (
                                                                <div className="text-warning mt-1">
                                                                    <i className="fa-solid fa-triangle-exclamation" /> Flagged as a possible duplicate of an existing receipt.
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="mt-2">
                                                            <button type="button" className="btn btn-sm btn-outline-success me-2" onClick={uploadAnother}>
                                                                Upload another for this member
                                                            </button>
                                                            <button type="button" className="btn btn-sm btn-success" onClick={() => navigate('/loyaltyReceipts')}>
                                                                View in queue
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadLoyaltyReceipt;
